import api from './api';

export interface SmartRecommendationResponse {
  recommendation: {
    card_id: number;
    card_name: string;
    bank_name: string;
    bank_operating_category?: 'TRADITIONAL' | 'NON_TRADITIONAL' | 'UNKNOWN';
    is_traditional_bank?: boolean;
    reward_type: 'cashback' | 'points' | 'miles';
    base_reward: number;
    points_earned?: number;
    estimated_cash_value?: number;
    currency_basis?: 'TTD' | 'USD';
    ttd_value?: number;
    reward_rate: number;
    category: string;
  amount: number;
    min_spend_met: boolean;
    min_spend?: number;
    max_reward_limit?: number;
    special_conditions?: string[];
    all_possible_rates?: Array<{
      rate: number;
      description: string;
      type: 'cashback' | 'points' | 'miles';
    }>;
  };
  all_cards_analysis: Array<{
    card_id: number;
    card_name: string;
    bank_name: string;
    bank_operating_category?: 'TRADITIONAL' | 'NON_TRADITIONAL' | 'UNKNOWN';
    is_traditional_bank?: boolean;
    reward_type: 'cashback' | 'points' | 'miles';
    base_reward: number;
    points_earned?: number;
    estimated_cash_value?: number;
    currency_basis?: 'TTD' | 'USD';
    ttd_value?: number;
    reward_rate: number;
    category: string;
    min_spend_met: boolean;
    min_spend?: number;
    max_reward_limit?: number;
    special_conditions?: string[];
    all_possible_rates?: Array<{
      rate: number;
      description: string;
      type: 'cashback' | 'points' | 'miles';
    }>;
  }>;
}

export interface SavingsAnalysis {
  period_days: number;
  total_spent: number;
  total_reward_earned: number;
  total_potential_reward: number;
  total_missed_savings: number;
  projected_annual_savings: number;
  by_category: { [key: string]: SpendingCategory };
  recommended_actions: Array<{
    merchant: string;
    amount: number;
    category: string;
    recommended_card: string;
    missed_savings: number;
    date: string;
  }>;
}

export interface SpendingCategory {
  name: string;
  total_spent: number;
  reward_earned: number;
  potential_reward: number;
  missed_savings: number;
  transaction_count: number;
}

export interface Transaction {
  id: number;
  date: string;
  merchant: string;
  amount: number;
  category: string;
  description?: string;
  card_name?: string;
  reward_earned: number;
  potential_reward: number;
  missed_savings: number;
}

export const transactionService = {
  getSmartRecommendations: async (
    amount: number,
    category: string,
    userCardIds: number[],
    merchant?: string,
    location?: string,
    transaction_location?: 'local' | 'international'
  ): Promise<SmartRecommendationResponse> => {
    const response = await api.post('/transactions/smart-recommendations/', {
      amount,
      category,
      user_card_ids: userCardIds,
      merchant,
      location,
      transaction_location: transaction_location || 'local',
    });
    return response.data;
  },

  getSavingsAnalysis: async (days: number = 30): Promise<SavingsAnalysis> => {
    const response = await api.get(`/transactions/savings-analysis/?days=${days}`);
    return response.data;
  },

  getTransactions: async (params?: {
    start_date?: string;
    end_date?: string;
    category?: string;
    limit?: number;
    page?: number;
    page_size?: number;
  }): Promise<{ transactions: Transaction[]; pagination?: any; summary?: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.page) queryParams.append('page', params.page.toString());
    // Use large page_size to get all transactions, or allow custom page_size
    const pageSize = params?.page_size || params?.limit || 1000; // Get all transactions by default
    queryParams.append('page_size', pageSize.toString());
    
    const response = await api.get(`/transactions/transactions/?${queryParams.toString()}`);
    const data = response.data;
    
    // Handle both array response (legacy) and object response (new format)
    if (Array.isArray(data)) {
      return {
        transactions: data.map((txn: any) => ({
          ...txn,
          card_name: txn.card?.name || txn.card_name || 'N/A',
        })),
      };
    }
    
    // Extract transactions array and map card object to card_name
    const transactions = (data.transactions || []).map((txn: any) => ({
      ...txn,
      card_name: txn.card?.name || txn.card_name || 'N/A',
    }));
    
    return {
      transactions,
      pagination: data.pagination,
      summary: data.summary,
    };
  },

  exportTransactions: async (format: 'csv' | 'json', params?: {
    start_date?: string;
    end_date?: string;
    category?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    queryParams.append('format', format);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.category) queryParams.append('category', params.category);
    
    try {
      const response = await api.get(`/transactions/export/?${queryParams.toString()}`, {
        responseType: 'blob',
      });
      
      // Check response status
      if (response.status >= 200 && response.status < 300) {
        // Check if response is actually a blob
        if (response.data instanceof Blob) {
          // Check content type to see if it's an error JSON
          const contentType = response.headers['content-type'] || '';
          if (contentType.includes('application/json')) {
            // It's JSON, likely an error - parse it
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.error || errorData.detail || 'Export failed');
          }
          return response.data;
        }
        throw new Error('Invalid response type from server');
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error: any) {
      // If error response is a blob containing JSON, parse it
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.detail || 'Export failed');
        } catch (parseError) {
          // Not JSON, rethrow original error
          throw new Error(error.message || 'Export failed');
        }
      }
      // If error response is already JSON
      if (error.response?.data && typeof error.response.data === 'object' && !(error.response.data instanceof Blob)) {
        throw new Error(error.response.data.error || error.response.data.detail || 'Export failed');
      }
      throw error;
    }
  },
};
